import { Box, Button, ButtonGroup, Card, Divider, FormHelp, Heading, Paragraph, Text } from '@zillow/constellation-10';

import RenewalVsTurnoverField from './RenewalVsTurnoverField';
import { calculateRenewalVsTurnover, formatCurrency, isCurrencyField } from './renewalVsTurnover';
import useRenewalVsTurnoverUrlState from './useRenewalVsTurnoverUrlState';

import type {
  CatchUp,
  RenewalVsTurnoverFieldName,
  RenewalVsTurnoverResult,
  RenewalVsTurnoverValues,
} from './renewalVsTurnover';

const DEFAULT_VALUES: RenewalVsTurnoverValues = {
  currentRent: '1,800',
  renewalRent: '1,875',
  newTenantRent: '2,000',
  vacancyMonths: '1',
  cleaningCost: '750',
  additionalCosts: '0',
};

const NO_TURNOVER_VALUES: RenewalVsTurnoverValues = {
  ...DEFAULT_VALUES,
  vacancyMonths: '0',
  cleaningCost: '0',
};

const CURRENT_RENT_FIELDS: Array<FieldConfig> = [
  {
    field: 'currentRent',
    label: 'Current monthly rent',
    help: 'Context only. Current rent doesn’t affect the comparison.',
  },
];

const RENT_OPTION_FIELDS: Array<FieldConfig> = [
  { field: 'renewalRent', label: 'Proposed renewal monthly rent' },
  { field: 'newTenantRent', label: 'New tenant monthly rent' },
];

const VACANCY_FIELDS: Array<FieldConfig> = [
  {
    field: 'vacancyMonths',
    label: 'Vacancy months',
    help: 'From 0 to 12 months. Partial months such as 0.5 are allowed.',
  },
];

const TURNOVER_COST_FIELDS: Array<FieldConfig> = [
  { field: 'cleaningCost', label: 'Cleaning and repairs' },
  { field: 'additionalCosts', label: 'Additional turnover costs' },
];

const SINGLE_FIELD_GRID_CSS = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  rowGap: 'zero',
} as const;

const FIELD_GRID_CSS = {
  ...SINGLE_FIELD_GRID_CSS,
  gridTemplateColumns: { base: '1fr', md: '1fr 1fr' },
  columnGap: '400',
  marginBlockEnd: '-400',
} as const;

const COLUMN_CSS = {
  display: 'flex',
  flexDirection: 'column',
  gap: '400',
  minWidth: 0,
  '& > :last-child': { flexGrow: 1 },
} as const;

const HELP_CSS = { marginBlockStart: '200', marginBlockEnd: '400' } as const;

const NO_VALUE = '—';

const getAdvantage = (retentionAdvantage: number) => {
  const roundedAdvantage = Math.round(retentionAdvantage);
  if (roundedAdvantage > 0) {
    return { label: 'Retention advantage', summary: 'More net rent in year one by renewing with your tenant.' };
  }
  if (roundedAdvantage < 0) {
    return { label: 'Turnover advantage', summary: 'More net rent in year one by finding a new tenant.' };
  }
  return { label: 'Same first-year net rent', summary: 'Both options produce the same net rent in year one.' };
};

const getCatchUpHeadline = (catchUp: CatchUp) => {
  switch (catchUp.status) {
    case 'future':
      return `Month ${catchUp.month.toLocaleString('en-US')}`;
    case 'immediate':
      return 'Immediately';
    case 'tied':
      return 'Both options stay tied';
    case 'never':
      return 'No catch-up';
  }
};

const getCatchUpSummary = (result: RenewalVsTurnoverResult) => {
  const { catchUp } = result;
  switch (catchUp.status) {
    case 'future':
      return `Turnover makes up the ${formatCurrency(result.upfrontTurnoverLoss)} in lost rent and costs with ${formatCurrency(result.monthlyPremium)} more rent per month. It catches up in month ${catchUp.month.toLocaleString('en-US')}, counting from your decision.`;
    case 'immediate':
      return 'With no vacancy or turnover costs, the higher new tenant rent puts turnover ahead right away.';
    case 'tied':
      return 'The rents are equal, with no lost rent or turnover costs. Neither option pulls ahead.';
    case 'never':
      return result.monthlyPremium === 0
        ? 'The rents are equal, so turnover can’t recover its lost rent and one-time costs.'
        : 'New tenant rent is lower than renewal rent, so turnover never pulls ahead under these assumptions.';
  }
};

type FieldConfig = {
  field: RenewalVsTurnoverFieldName;
  label: string;
  help?: string;
};

type SummaryRow = {
  label: string;
  value: number | undefined;
};

const SummaryList = ({ rows }: { rows: Array<SummaryRow> }) => (
  <Box asChild css={{ margin: 0 }}>
    <dl>
      {rows.map(({ label, value }) => (
        <Box
          key={label}
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '400',
            paddingBlock: '200',
          }}
        >
          <Text textStyle="body-sm" asChild>
            <dt>{label}</dt>
          </Text>
          <Text textStyle="body-sm-bold" asChild>
            <dd>{value === undefined ? NO_VALUE : formatCurrency(value)}</dd>
          </Text>
        </Box>
      ))}
    </dl>
  </Box>
);

const RenewalVsTurnoverCalculator = () => {
  const { values, setValues } = useRenewalVsTurnoverUrlState(DEFAULT_VALUES);

  const calculation = calculateRenewalVsTurnover(values);
  const { errors } = calculation;

  const renderFields = (fields: Array<FieldConfig>) => (
    <Box css={fields.length > 1 ? FIELD_GRID_CSS : SINGLE_FIELD_GRID_CSS}>
      {fields.map(({ field, label, help }) => (
        <RenewalVsTurnoverField
          key={field}
          label={label}
          unit={isCurrencyField(field) ? '$' : 'months'}
          help={help}
          value={values[field]}
          error={errors[field]}
          onChange={(value) => setValues((previous) => ({ ...previous, [field]: value }))}
        />
      ))}
    </Box>
  );

  const renderResults = (result: RenewalVsTurnoverResult | undefined) => {
    const advantage = result ? getAdvantage(result.retentionAdvantage) : undefined;

    return (
      <>
        <Card elevated={false}>
          <Heading level={3} textStyle="body-lg-bold">
            Your first-year comparison
          </Heading>

          <Box aria-live="polite" css={{ marginBlock: '400', padding: '400', backgroundColor: 'bg.screen.softest' }}>
            <Text textStyle="body-sm">{advantage?.label ?? 'First-year advantage'}</Text>
            <Text textStyle="heading-xs" css={{ display: 'block', marginBlockStart: '200' }}>
              {result ? formatCurrency(Math.abs(result.retentionAdvantage)) : NO_VALUE}
            </Text>
            {advantage && <Paragraph textStyle="body-sm">{advantage.summary}</Paragraph>}
          </Box>

          <SummaryList
            rows={[
              { label: 'Renewal net rent', value: result?.renewalFirstYear },
              { label: 'Turnover net rent', value: result?.turnoverFirstYear },
            ]}
          />

          <FormHelp css={{ marginBlockStart: '200' }}>
            Net rent here is rent after the one-time turnover costs entered, not total property cash flow.
          </FormHelp>

          <Divider css={{ marginBlock: '400' }} />

          <Text textStyle="body-bold">Where turnover costs add up</Text>

          <SummaryList
            rows={[
              { label: '12 months at new tenant rent', value: result?.newTenantFullYear },
              { label: 'Less rent lost to vacancy', value: result?.vacancyLoss },
              { label: 'Less cleaning and repairs', value: result?.cleaningCost },
              { label: 'Less additional costs', value: result?.additionalCosts },
            ]}
          />
        </Card>

        <Card elevated={false}>
          <Heading level={3} textStyle="body-lg-bold">
            When does turnover catch up?
          </Heading>
          <Box aria-live="polite">
            <Text textStyle="heading-xs" css={{ display: 'block', marginBlockStart: '300' }}>
              {result ? getCatchUpHeadline(result.catchUp) : NO_VALUE}
            </Text>
            {result && (
              <Paragraph textStyle="body-sm" css={{ marginBlockStart: '200' }}>
                {getCatchUpSummary(result)}
              </Paragraph>
            )}
          </Box>
        </Card>
      </>
    );
  };

  return (
    <Box
      css={{
        display: 'grid',
        gridTemplateColumns: { base: '1fr', lg: '3fr 2fr' },
        gap: '400',
        width: '100%',
      }}
    >
      <Card elevated={false} css={{ minWidth: 0 }}>
        <Heading level={3} textStyle="body-lg-bold">
          Compare your rent options
        </Heading>

        <Paragraph textStyle="body-sm" css={{ marginBlockEnd: '400' }}>
          Use the monthly rent you’d charge for each option.
        </Paragraph>

        {renderFields(CURRENT_RENT_FIELDS)}

        {renderFields(RENT_OPTION_FIELDS)}

        {calculation.ok && calculation.result.rentChange && (
          <FormHelp css={HELP_CSS}>
            Renewal changes your current rent by {formatCurrency(calculation.result.rentChange.renewal)} per month. A
            new tenant changes it by {formatCurrency(calculation.result.rentChange.newTenant)}.
          </FormHelp>
        )}

        <Divider css={{ marginBlock: '400' }} />

        <Heading level={3} textStyle="body-lg-bold">
          Account for turnover
        </Heading>

        <Paragraph textStyle="body-sm" css={{ marginBlockEnd: '400' }}>
          Include only costs you’d incur to replace the tenant.
        </Paragraph>

        {renderFields(VACANCY_FIELDS)}

        {renderFields(TURNOVER_COST_FIELDS)}

        <FormHelp css={HELP_CSS}>
          Cleaning and repairs may include painting. Additional costs may include leasing fees, advertising, or other
          one-time costs. Don’t add lost rent here. Vacancy already accounts for it.
        </FormHelp>

        <ButtonGroup aria-label="Example values" size="sm" css={{ flexWrap: 'wrap' }}>
          <Button tone="brand" emphasis="outlined" onClick={() => setValues(DEFAULT_VALUES)}>
            Reset example
          </Button>
          <Button tone="brand" emphasis="bare" onClick={() => setValues(NO_TURNOVER_VALUES)}>
            Try no vacancy or costs
          </Button>
        </ButtonGroup>
      </Card>

      <Box css={COLUMN_CSS}>{renderResults(calculation.ok ? calculation.result : undefined)}</Box>
    </Box>
  );
};

export default RenewalVsTurnoverCalculator;
